// @erp/module-cms/schemas — the TENDERS content type wire contracts. The RICHEST
// content type: a SIMPLE single-document notice (isSimple) or a COMPLEX procurement
// (a critical-dates timeline, EMD/fee/covers, billing+shipping address, pre-
// qualification), a reference-number engine, corrigendum→parent linkage, and a
// companion-announcement auto-sync. Browser-safe: zod + plain data only (fe forms and
// the gateway parse the SAME schemas). Feature-referenced from MBSPSU (READ-ONLY) —
// rebuilt for the target stack, English-first + locale-ready (a translation is a
// SIBLING ROW under a different `locale`, never a *_hi/*_pa column explosion).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ SECURITY — body_html is stored RAW and is NOT sanitized on write (the 0004     │
// │ announcements law). Sanitization happens ON THE WAY OUT: every /public/*        │
// │ route passes bodyHtml through the allowlist sanitiser (api/sanitize.ts,         │
// │ wired 2026-09-13 per ADR-010 §2.4) before it leaves the ERP. Admin reads        │
// │ carry the raw column; content is authored ONLY by trusted cms:tenders holders.  │
// ├──────────────────────────────────────────────────────────────────────────────┤
// │ DEFERRED (documented, NOT built): a private/PIN + HMAC signed-asset gate is a   │
// │ CROSS-CUTTING "private records" slice tenders/events/publications will share —  │
// │ so there is NO privatePin / visibilitySchedule here and status is               │
// │ draft|published|archived only (no 'private' yet). Revisions + the public read-  │
// │ split are likewise deferred to their own waves.                                 │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs, Paise } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { AnnouncementAttachment, ATTACHMENTS_MAX, BODY_HTML_MAX, SUMMARY_MAX } from "./announcements.js";
import { PinField, RecordAccessLevel } from "./private.js";
import { PublishingStatus, RefNoSeqType } from "./settings.js";
import type { RefNoSeqType as RefNoSeqTypeT } from "./settings.js";

// NOTE: slugify + SLUG_MAX/SLUG_RE are REUSED from the categories slice (the ONE slug
// grammar + deriver) and BODY_HTML_MAX / the AnnouncementAttachment shape from the
// announcements slice — one source, never a re-declaration.

// ── The publishing status enum — REUSED from settings (draft|published|archived),
// so the tender lifecycle and the site-wide default_publishing_status can never drift
// onto different value sets (the announcements precedent). ───────────────────────────
export const TenderStatus = PublishingStatus;
export type TenderStatus = z.infer<typeof TenderStatus>;

// ═══════════════ Complex-procurement structured sub-objects (serialized to *_json) ══════════════
// Each is a zod object stored as one nullable TEXT column — nullable because a SIMPLE
// tender carries none of them. Kept STRICT-ish (bounded strings, integer paise) so a
// runaway blob can never bloat the row. All fields optional inside the blob (a partially-
// filled procurement form is valid).

/** The procurement timeline — every milestone as an OPTIONAL epoch-ms (the db-lib time law; a calendar date is still an instant). */
export const TenderCriticalDates = z.object({
  ePublishedAt: EpochMs.optional(),
  docDownloadStartAt: EpochMs.optional(),
  docDownloadEndAt: EpochMs.optional(),
  clarificationStartAt: EpochMs.optional(),
  clarificationEndAt: EpochMs.optional(),
  preBidMeetingAt: EpochMs.optional(),
  bidSubmissionStartAt: EpochMs.optional(),
  bidSubmissionEndAt: EpochMs.optional(),
  bidOpeningAt: EpochMs.optional(),
});
export type TenderCriticalDates = z.infer<typeof TenderCriticalDates>;

/** Money block — EMD / document fee / estimated value in INTEGER PAISE (never floats; the db-lib money law) + EMD-exemption note. */
export const TenderFinancial = z.object({
  emdPaise: Paise.optional(),
  feePaise: Paise.optional(),
  tenderValuePaise: Paise.optional(),
  exemptionDetails: z.string().trim().max(2000).optional(),
});
export type TenderFinancial = z.infer<typeof TenderFinancial>;

/** One postal address (billing or shipping). */
export const TenderAddressParts = z.object({
  line1: z.string().trim().max(300).optional(),
  line2: z.string().trim().max(300).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().trim().max(120).optional(),
  pinCode: z.string().trim().max(20).optional(),
  country: z.string().trim().max(120).optional(),
});
export type TenderAddressParts = z.infer<typeof TenderAddressParts>;

export const TenderAddress = z.object({
  billing: TenderAddressParts.optional(),
  shipping: TenderAddressParts.optional(),
});
export type TenderAddress = z.infer<typeof TenderAddress>;

/** A bid-cover envelope (e.g. "Technical Bid" / "Financial Bid"). */
export const TenderCover = z.object({
  title: z.string().trim().min(1).max(200),
  type: z.string().trim().max(50).optional(),
});
export type TenderCover = z.infer<typeof TenderCover>;
export const COVERS_MAX = 20;

/** Pre-qualification criteria + contract form + completion period. */
export const TenderPrequalification = z.object({
  criteria: z.string().trim().max(5000).optional(),
  formOfContract: z.string().trim().max(200).optional(),
  periodOfCompletion: z.string().trim().max(200).optional(),
});
export type TenderPrequalification = z.infer<typeof TenderPrequalification>;

// ══════════════════════════════ REF-NO ENGINE (pure helpers) ═══════════════════════════════
// The reference-number template engine, rebuilt from MBSPSU formatRefNoPlaceholders /
// formatSequence. PURE + browser-safe so the admin form can PREVIEW the next ref-no with
// the same code the server mints. The counter increment itself is NOT here — it is an
// ATOMIC D1 statement in the repo (the money-race hot path); these helpers only FORMAT a
// number the repo already minted.

/** The formatting config read (non-atomically) from cms_setting — the counter is separate + atomic. */
export interface RefNoConfig {
  prefix: string;
  postfix: string;
  seqType: RefNoSeqTypeT;
  seqLength: number;
}

/** Pull the formatting config out of a merged settings map (defaults when a key is absent/invalid). */
export const refNoConfigFrom = (settings: Record<string, string>): RefNoConfig => ({
  prefix: settings.ref_no_prefix ?? "",
  postfix: settings.ref_no_postfix ?? "",
  seqType: RefNoSeqType.safeParse(settings.ref_no_seq_type).data ?? "numeric",
  seqLength: (() => {
    const n = Number(settings.ref_no_seq_length);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 6;
  })(),
});

/**
 * Resolve the date placeholders in a prefix/postfix against an epoch-ms instant (UTC —
 * a Worker runs in UTC; tests pin a fixed epoch). YYYY-YY / YY-YY expand to the Indian
 * financial year (Apr–Mar), so the longer tokens are replaced FIRST (order matters).
 */
export const resolveRefNoPlaceholders = (str: string, at: number): string => {
  if (!str) return "";
  const d = new Date(at);
  const yyyy = String(d.getUTCFullYear());
  const yy = yyyy.slice(-2);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  // Financial year: Jan–Mar (month index 0..2) belongs to the PREVIOUS FY start.
  const isJanToMar = d.getUTCMonth() < 3;
  const fyStart = isJanToMar ? d.getUTCFullYear() - 1 : d.getUTCFullYear();
  const fyEnd = fyStart + 1;
  const fyLong = `${fyStart}-${String(fyEnd).slice(-2)}`;
  const fyShort = `${String(fyStart).slice(-2)}-${String(fyEnd).slice(-2)}`;
  return str
    .replace(/YYYY-YY/g, fyLong)
    .replace(/YY-YY/g, fyShort)
    .replace(/YYYY/g, yyyy)
    .replace(/YY/g, yy)
    .replace(/MM/g, mm)
    .replace(/DD/g, dd);
};

/**
 * Format a sequence number per its type + padding length (MBSPSU formatSequence, ported):
 * numeric → decimal, alphabetic → bijective base-26 (A, B … Z, AA …), alphanumeric →
 * base-36 uppercase. BigInt so a huge counter never loses precision.
 */
export const formatRefNoSequence = (value: number, seqType: RefNoSeqTypeT, seqLength: number): string => {
  let val: bigint;
  try {
    val = BigInt(Math.trunc(value));
    if (val < 0n) val = 0n;
  } catch {
    val = 0n;
  }
  const pad = Math.max(1, Math.trunc(seqLength) || 1);
  if (seqType === "alphabetic") {
    let result = "";
    let temp = val;
    while (temp > 0n) {
      temp -= 1n;
      result = String.fromCharCode(65 + Number(temp % 26n)) + result;
      temp /= 26n;
    }
    return (result || "A").padStart(pad, "A");
  }
  if (seqType === "alphanumeric") {
    return val.toString(36).toUpperCase().padStart(pad, "0");
  }
  return val.toString().padStart(pad, "0");
};

/** Compose a full reference number: resolved prefix + padded sequence + resolved postfix. */
export const buildRefNo = (cfg: RefNoConfig, seqNumber: number, at: number): string =>
  `${resolveRefNoPlaceholders(cfg.prefix, at)}${formatRefNoSequence(seqNumber, cfg.seqType, cfg.seqLength)}${resolveRefNoPlaceholders(cfg.postfix, at)}`;

// ── The write-side field validators (one source, Create/Update both derive) ─────────
const tenderCore = {
  title: z.string().trim().min(1).max(300),
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** RAW HTML — sanitized ONLY at the website render (HTMLRewriter), never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  summary: z.string().trim().max(SUMMARY_MAX).nullable(),
  status: TenderStatus,
  /** Explicit publish instant (epoch-ms). Usually left unset — the service stamps it when status→published. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** FK to a cms_category of type='tender' — the service validates the cross-entity ref. Nullable to clear. */
  categoryId: EntityId.nullable(),
  /** 1 = simple single-document notice; 0 = complex procurement (the structured blobs apply). */
  isSimple: z.boolean(),
  criticalDates: TenderCriticalDates.nullable(),
  financial: TenderFinancial.nullable(),
  address: TenderAddress.nullable(),
  covers: z.array(TenderCover).max(COVERS_MAX).nullable(),
  prequalification: TenderPrequalification.nullable(),
  attachments: z.array(AnnouncementAttachment).max(ATTACHMENTS_MAX).nullable(),
  /** The row's locale — a canonical BCP 47 tag (./locale.ts, the locale contract). */
  locale: Locale,
};

/**
 * Create body — title required; slug OPTIONAL (the service derives it via slugify(title),
 * id-fallback on a non-sluggable title). status/locale/isSimple carry the DDL defaults.
 *
 * refNo is OPTIONAL and normally OMITTED — a top-level tender is minted a fresh number
 * from the atomic counter. A supplied refNo is used verbatim (legacy import), still
 * backstopped by the partial UNIQUE(ref_no). parentId marks this a CORRIGENDUM: its
 * ref_no stays NULL (it references the parent), it consumes NO sequence number, and the
 * service resolves parent_ref_no + corrigendum_no. createAnnouncement is a ROUTING flag
 * (not a column) — publish auto-posts a companion announcement unless it is false (and
 * the tender_auto_post_announcement setting is the fallback default).
 */
export const CreateCmsTender = z.object({
  title: tenderCore.title,
  slug: tenderCore.slug.optional(),
  bodyHtml: tenderCore.bodyHtml.optional(),
  summary: tenderCore.summary.optional(),
  status: tenderCore.status.default("draft"),
  publishedAt: tenderCore.publishedAt.optional(),
  categoryId: tenderCore.categoryId.optional(),
  isSimple: tenderCore.isSimple.default(true),
  criticalDates: tenderCore.criticalDates.optional(),
  financial: tenderCore.financial.optional(),
  address: tenderCore.address.optional(),
  covers: tenderCore.covers.optional(),
  prequalification: tenderCore.prequalification.optional(),
  attachments: tenderCore.attachments.optional(),
  refNo: z.string().trim().min(1).max(120).nullable().optional(),
  parentId: EntityId.optional(),
  locale: tenderCore.locale.default("en"),
  createAnnouncement: z.boolean().optional(),
  // ── Private-records slice (0011): access_level + the WRITE-ONLY pin. A create with
  // accessLevel='private' MUST carry a pin (the service 400s otherwise); pin is hashed
  // server-side and NEVER echoed back on any DTO (schemas/private.ts). ──
  accessLevel: RecordAccessLevel.default("public"),
  pin: PinField.optional(),
});
export type CreateCmsTender = z.infer<typeof CreateCmsTender>;

/**
 * Update body — a partial PATCH over the editable fields (slug stays STABLE on rename; a
 * slug collision still maps to 409). refNo + parentId are IMMUTABLE after creation
 * (a minted number and the corrigendum linkage never change), so they are omitted here.
 */
export const UpdateCmsTender = z
  .object({
    title: tenderCore.title,
    slug: tenderCore.slug,
    bodyHtml: tenderCore.bodyHtml,
    summary: tenderCore.summary,
    status: tenderCore.status,
    publishedAt: tenderCore.publishedAt,
    categoryId: tenderCore.categoryId,
    isSimple: tenderCore.isSimple,
    criticalDates: tenderCore.criticalDates,
    financial: tenderCore.financial,
    address: tenderCore.address,
    covers: tenderCore.covers,
    prequalification: tenderCore.prequalification,
    attachments: tenderCore.attachments,
    locale: tenderCore.locale,
    createAnnouncement: z.boolean(),
    // Private-records slice (0011): flip access_level and/or (re)set the write-only pin.
    // Omitting pin while staying private PRESERVES the stored pin; switching to public
    // NULLs it (service.ts). pin is never returned on a DTO.
    accessLevel: RecordAccessLevel,
    pin: PinField,
  })
  .partial();
export type UpdateCmsTender = z.infer<typeof UpdateCmsTender>;

/** List query — keyset cursor+limit + optional ?status ?categoryId ?isSimple ?parentId ?locale filters (soft-deleted rows always excluded). */
export const CmsTenderListQuery = CursorQuery.extend({
  status: TenderStatus.optional(),
  categoryId: EntityId.optional(),
  /** Exact-eq on the row's locale (the locale contract) — the one list that lacked it until 2026-09-14; omitted lists every locale. */
  locale: Locale.optional(),
  /** "1" simple / "0" complex — coerced to the stored INTEGER on the way to SQL. */
  isSimple: z.enum(["0", "1"]).optional(),
  /** Only the corrigendums of a given parent tender. */
  parentId: EntityId.optional(),
  /** Opt-in TRASH view — true lists soft-deleted rows only; omitted (the default) lists live rows. Coerced + optional so a stray value never 422s the query. */
  // Self-healing like the FE's ?trashed: only "1"/"true" opt into trash; anything else
  // (absent, "false", "0", garbage) heals to the live list — never flips to trash or 422s.
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsTenderListQuery = z.infer<typeof CmsTenderListQuery>;

/**
 * The wire DTO — the read shape (camelCase; audit actor columns stay server-side).
 * The structured blobs surface as parsed objects; `parentRefNo` is the resolved parent's
 * ref_no (a corrigendum convenience, not a stored column); `deletedAt` surfaces so the
 * admin trash view can tell live rows from trashed ones.
 */
export const CmsTender = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  refNo: z.string().nullable(),
  isSimple: z.boolean(),
  title: z.string(),
  bodyHtml: z.string().nullable(),
  summary: z.string().nullable(),
  status: TenderStatus,
  publishedAt: EpochMs.nullable(),
  categoryId: EntityId.nullable(),
  criticalDates: TenderCriticalDates.nullable(),
  financial: TenderFinancial.nullable(),
  address: TenderAddress.nullable(),
  covers: z.array(TenderCover),
  prequalification: TenderPrequalification.nullable(),
  parentId: EntityId.nullable(),
  parentRefNo: z.string().nullable(),
  corrigendumNo: z.number().int().nullable(),
  attachments: z.array(AnnouncementAttachment),
  companionAnnouncementId: EntityId.nullable(),
  // Private-records slice (0011): the access level + a DERIVED lock flag (is_locked =
  // accessLevel === 'private'). NEVER the pin/pin_hash. On the anonymous public read a
  // locked record's body + assets are withheld; the admin read carries the full row.
  accessLevel: RecordAccessLevel,
  isLocked: z.boolean(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsTender = z.infer<typeof CmsTender>;
